import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareText, X, Send, ArrowRight } from "lucide-react";
import { BRAND } from "../lib/brand";
import { useT } from "../lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  atvs: "",
  sxs: "",
  seater: "",
  people: "",
  kids: "",
  duration: "",
  datetime: "",
};

function composeMessage(f, lang) {
  if (lang === "es") {
    return (
      `¡Hola Rockstar ATV Rentals! Me gustaría reservar un tour.\n\n` +
      `• Nombre: ${f.fullName || "—"}\n` +
      `• Email: ${f.email || "—"}\n` +
      `• Teléfono: ${f.phone || "—"}\n` +
      `• ATVs (Rancher 420): ${f.atvs || "—"}\n` +
      `• Side-by-Sides (Talon 1000): ${f.sxs || "—"}\n` +
      `• Plazas (2 o 4): ${f.seater || "—"}\n` +
      `• Personas en total: ${f.people || "—"}\n` +
      `• Niños y edades: ${f.kids || "Ninguno"}\n` +
      `• Duración (1 o 2 hr): ${f.duration || "—"}\n` +
      `• Fecha y hora preferidas: ${f.datetime || "—"}\n\n` +
      `Por favor confirmen disponibilidad. ¡Gracias!`
    );
  }
  return (
    `Hi Rockstar ATV Rentals! I'd like to book a ride.\n\n` +
    `• Name: ${f.fullName || "—"}\n` +
    `• Email: ${f.email || "—"}\n` +
    `• Phone: ${f.phone || "—"}\n` +
    `• ATVs (Rancher 420): ${f.atvs || "—"}\n` +
    `• Side-by-Sides (Talon 1000): ${f.sxs || "—"}\n` +
    `• Seater (2 or 4): ${f.seater || "—"}\n` +
    `• Total people: ${f.people || "—"}\n` +
    `• Kids & ages: ${f.kids || "None"}\n` +
    `• Tour length (1 or 2 hr): ${f.duration || "—"}\n` +
    `• Preferred date & time: ${f.datetime || "—"}\n\n` +
    `Please confirm availability. Thanks!`
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const PHONE_DIGITS_RE = /\d/g;

export default function BookViaText({ open, onClose }) {
  const { t, lang } = useT();
  const [form, setForm] = useState(initialForm);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const smsHref = `sms:${BRAND.phoneRaw}?body=${encodeURIComponent(composeMessage(form, lang))}`;

  const validate = () => {
    if (!form.fullName.trim() || !form.email.trim() || !form.phone.trim()) {
      return t("booking.requiredError");
    }
    if (!EMAIL_RE.test(form.email.trim())) return t("booking.invalidEmail");
    const digits = (form.phone.match(PHONE_DIGITS_RE) || []).length;
    if (digits < 10) return t("booking.invalidPhone");
    return "";
  };

  const sendLead = () => {
    try {
      fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          atvs: form.atvs || "",
          sxs: form.sxs || "",
          seater: form.seater || "",
          people: form.people || "",
          kids: form.kids || "",
          duration: form.duration || "",
          datetime: form.datetime || "",
          composed_message: composeMessage(form, lang),
        }),
      }).catch(() => {});
    } catch (_) {
      /* noop */
    }
  };

  const handleSend = (e) => {
    const problem = validate();
    if (problem) {
      e.preventDefault();
      setErr(problem);
      return;
    }
    setErr("");
    sendLead();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          data-testid="book-text-modal"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl bg-[color:var(--bg-elev)] border border-white/15 overflow-hidden text-white"
          >
            <div className="flex items-center justify-between px-5 md:px-6 py-3 bg-black border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <MessageSquareText size={16} className="text-[color:var(--primary)]" />
                <p className="font-display text-white text-base tracking-wide">{t("booking.title")}</p>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:text-[color:var(--primary)] transition-colors"
                aria-label={t("booking.close")}
                data-testid="book-text-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 md:p-6 max-h-[75vh] overflow-y-auto" data-lenis-prevent>
              <p className="text-xs md:text-sm text-white/60 mb-5">{t("booking.subtitle")}</p>

              <div className="grid md:grid-cols-2 gap-3">
                <Field label={t("booking.fullName")} full>
                  <input
                    type="text"
                    required
                    value={form.fullName}
                    onChange={(e) => set("fullName", e.target.value)}
                    placeholder={lang === "es" ? "Nombre completo" : "Full name"}
                    className={inputCls}
                    data-testid="book-input-fullname"
                  />
                </Field>
                <Field label={t("booking.email")}>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@example.com"
                    className={inputCls}
                    data-testid="book-input-email"
                  />
                </Field>
                <Field label={t("booking.phone")}>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="(407) 555-0123"
                    className={inputCls}
                    data-testid="book-input-phone"
                  />
                </Field>
                <Field label={t("booking.atvs")}>
                  <input
                    type="number"
                    min="0"
                    value={form.atvs}
                    onChange={(e) => set("atvs", e.target.value)}
                    placeholder="e.g. 2"
                    className={inputCls}
                    data-testid="book-input-atvs"
                  />
                </Field>
                <Field label={t("booking.sxs")}>
                  <input
                    type="number"
                    min="0"
                    value={form.sxs}
                    onChange={(e) => set("sxs", e.target.value)}
                    placeholder="e.g. 1"
                    className={inputCls}
                    data-testid="book-input-sxs"
                  />
                </Field>
                <Field label={t("booking.seater")}>
                  <select
                    value={form.seater}
                    onChange={(e) => set("seater", e.target.value)}
                    className={inputCls}
                    data-testid="book-input-seater"
                  >
                    <option value="">{lang === "es" ? "Elegir…" : "Select…"}</option>
                    <option value={t("booking.seaterOptions")[1]}>{t("booking.seaterOptions")[1]}</option>
                    <option value={t("booking.seaterOptions")[2]}>{t("booking.seaterOptions")[2]}</option>
                    <option value={t("booking.seaterOptions")[3]}>{t("booking.seaterOptions")[3]}</option>
                  </select>
                </Field>
                <Field label={t("booking.people")}>
                  <input
                    type="number"
                    min="1"
                    value={form.people}
                    onChange={(e) => set("people", e.target.value)}
                    placeholder="e.g. 5"
                    className={inputCls}
                    data-testid="book-input-people"
                  />
                </Field>
                <Field label={t("booking.kids")} full>
                  <input
                    type="text"
                    value={form.kids}
                    onChange={(e) => set("kids", e.target.value)}
                    placeholder={lang === "es" ? "e.g. Sí — 8 y 11" : "e.g. Yes — 8 and 11"}
                    className={inputCls}
                    data-testid="book-input-kids"
                  />
                </Field>
                <Field label={t("booking.duration")}>
                  <select
                    value={form.duration}
                    onChange={(e) => set("duration", e.target.value)}
                    className={inputCls}
                    data-testid="book-input-duration"
                  >
                    <option value="">{lang === "es" ? "Elegir…" : "Select…"}</option>
                    <option value={t("booking.durationOptions")[1]}>{t("booking.durationOptions")[1]}</option>
                    <option value={t("booking.durationOptions")[2]}>{t("booking.durationOptions")[2]}</option>
                  </select>
                </Field>
                <Field label={t("booking.datetime")}>
                  <input
                    type="text"
                    value={form.datetime}
                    onChange={(e) => set("datetime", e.target.value)}
                    placeholder="e.g. Sat 3/16 · 4:00 PM"
                    className={inputCls}
                    data-testid="book-input-datetime"
                  />
                </Field>
              </div>

              <div className="mt-5 border border-white/10 bg-black p-3 text-[11px] text-white/60 leading-relaxed">
                <p className="font-mono font-bold uppercase tracking-widest text-white text-[10px] mb-1.5">
                  {t("booking.preview")}
                </p>
                <pre className="whitespace-pre-wrap font-body" data-testid="book-text-preview">
                  {composeMessage(form, lang)}
                </pre>
              </div>

              {err && (
                <p
                  className="mt-3 text-[color:var(--primary)] text-sm font-semibold"
                  data-testid="book-text-error"
                >
                  {err}
                </p>
              )}

              <a
                href={smsHref}
                onClick={handleSend}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-[0.16em] text-[11px] px-4 py-3.5 transition-colors"
                data-testid="book-text-send"
              >
                <Send size={13} /> {t("booking.send")} — {BRAND.phone} <ArrowRight size={13} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)] transition-colors";

function Field({ label, children, full = false }) {
  return (
    <label className={`${full ? "md:col-span-2" : ""} block`}>
      <span className="text-[9px] uppercase tracking-[0.26em] font-mono font-bold text-white/80 block mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
